import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { GenerateHtmlJson, HtmlJsonMeta, META_KEY } from './generateHtmlJson.js';
import { splitByPlaceholders } from './loaderJsonHelper.js';

type PlainJsonObject = { [key: string]: unknown };

const TYPE_HTML = 's';
const TYPE_JSONKEY = 'k';
const TYPE_JSONKEY_SKIP = 'K';
const TYPE_PLACEHOLDER = 'p';
const TYPE_PLACEHOLDER_SKIP = 'P';
const TYPE_SLOTHOLDER = 'h';
const TYPE_SLOTHOLDER_SKIP = 'H';
const TYPE_ARRAYBLOCK = 'a';
const TYPE_ARRAYBLOCK_SKIP = 'A';
const TYPE_SLOTHTML = 'x';
const TYPE_SLOT = 'i';
const TYPE_CONDITIONAL = 'c';

interface ParseResult {
    result: PlainJsonObject;
    consumed: number;
}

interface GenerateHtmlJsonParseOwner {
    convertHtmlToJson(htmlContent: string): { content: JsonObject };
    parseArrayBlockContentNew(parts: string[], startIndex: number, arrayKey: string, isNegative: boolean, meta: HtmlJsonMeta): ParseResult;
    parseArrayContentRecursiveNew(parts: string[], startIndex: number, arrayKey: string, meta: HtmlJsonMeta): ParseResult;
    parseConditionalBlockContentNew(parts: string[], startIndex: number, conditionalKey: string, meta: HtmlJsonMeta): ParseResult;
    parseSlotContentNew(parts: string[], startIndex: number, slotName: string, meta: HtmlJsonMeta): ParseResult;
    parseTemplateContentNew(parts: string[], startIndex: number, meta: HtmlJsonMeta): ParseResult;
}

function parseArrayContentRecursiveNewImpl(owner: GenerateHtmlJsonParseOwner, parts: string[], startIndex: number, arrayKey: string, meta: HtmlJsonMeta): ParseResult {
    const content: PlainJsonObject = {};
    let sequenceCounter = 1;
    let i = startIndex;

    while (i < parts.length) {
        const part: string = parts[i];

        if (!part) {
            i++;
            continue;
        }

        if (part.toLowerCase() === ('{{/' + arrayKey + '}}').toLowerCase()) {
            i++;
            break;
        }

        if (part.startsWith('{{?') && part.endsWith('}}')) {
            const conditionalKey: string = part.substring(3, part.length - 2);
            i++;

            const condMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
            const { result: conditionalBlockStructure, consumed: conditionalConsumed } = owner.parseConditionalBlockContentNew(parts, i, conditionalKey, condMeta);
            i += conditionalConsumed;

            let lookAheadIdx: number = i;
            let whitespaceBetween = '';
            while (lookAheadIdx < parts.length && (!parts[lookAheadIdx] || !parts[lookAheadIdx].trim())) {
                if (parts[lookAheadIdx]) {
                    whitespaceBetween += parts[lookAheadIdx];
                }
                lookAheadIdx++;
            }

            if (lookAheadIdx < parts.length && parts[lookAheadIdx] === '{{^' + conditionalKey + '}}') {
                if (whitespaceBetween) {
                    conditionalBlockStructure['WhitespaceBetween'] = whitespaceBetween;
                }
                i = lookAheadIdx + 1;

                const negCondMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: negBlockStructure, consumed: negConsumed } = owner.parseConditionalBlockContentNew(parts, i, conditionalKey, negCondMeta);
                i += negConsumed;
                conditionalBlockStructure['NegativeBlock'] = negBlockStructure;
            }

            const key = `${TYPE_CONDITIONAL}${sequenceCounter}_${conditionalKey}`;
            content[key] = conditionalBlockStructure;
            sequenceCounter++;
        } else if (part.startsWith('{{@') && part.endsWith('}}')) {
            const nestedArrayKey: string = part.substring(3, part.length - 2);
            if (!nestedArrayKey.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                i++;

                const arrayBlockMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: arrayMainBlock, consumed: arrayMainConsumed } = owner.parseArrayBlockContentNew(parts, i, nestedArrayKey, false, arrayBlockMeta);
                i += arrayMainConsumed;

                const mainWithMeta: PlainJsonObject = {};
                mainWithMeta[META_KEY] = arrayBlockMeta;
                for (const key of Object.keys(arrayMainBlock)) {
                    mainWithMeta[key] = arrayMainBlock[key];
                }

                const arrayBlockData: PlainJsonObject = { MainBlock: mainWithMeta };

                let lookAheadIdx: number = i;
                let whitespaceBetween = '';
                while (lookAheadIdx < parts.length && (!parts[lookAheadIdx] || !parts[lookAheadIdx].trim())) {
                    if (parts[lookAheadIdx]) {
                        whitespaceBetween += parts[lookAheadIdx];
                    }
                    lookAheadIdx++;
                }

                if (lookAheadIdx < parts.length && parts[lookAheadIdx] === '{{^' + nestedArrayKey + '}}') {
                    if (whitespaceBetween) {
                        arrayBlockData['WhitespaceBetween'] = whitespaceBetween;
                    }
                    i = lookAheadIdx + 1;

                    const negArrayMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: negArrayBlock, consumed: negArrayConsumed } = owner.parseArrayBlockContentNew(parts, i, nestedArrayKey, true, negArrayMeta);
                    i += negArrayConsumed;

                    const negWithMeta: PlainJsonObject = {};
                    negWithMeta[META_KEY] = negArrayMeta;
                    for (const key of Object.keys(negArrayBlock)) {
                        negWithMeta[key] = negArrayBlock[key];
                    }
                    arrayBlockData['NegativeBlock'] = negWithMeta;
                }

                const key = `${TYPE_ARRAYBLOCK}${sequenceCounter}_${nestedArrayKey}`;
                content[key] = arrayBlockData;
                sequenceCounter++;
                GenerateHtmlJson.addArrayBlockToMeta(meta, nestedArrayKey);
            } else {
                const key = `${TYPE_HTML}${sequenceCounter}`;
                content[key] = part;
                sequenceCounter++;
                i++;
            }
        } else if (part.startsWith('{{!$') && part.endsWith('}}')) {
            const jsonKeyName: string = part.substring(4, part.length - 2);
            const key = `${TYPE_JSONKEY_SKIP}${sequenceCounter}_${jsonKeyName}`;
            content[key] = part;
            sequenceCounter++;
            i++;
        } else if (part.startsWith('{{$') && part.endsWith('}}')) {
            const jsonKeyName: string = part.substring(3, part.length - 2);
            const key = `${TYPE_JSONKEY}${sequenceCounter}_${jsonKeyName}`;
            content[key] = part;
            sequenceCounter++;
            GenerateHtmlJson.addJsonKeyToMeta(meta, jsonKeyName);
            i++;
        } else if (part.startsWith('{{!') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('$') && !part.includes('?')) {
            const placeholderName: string = part.substring(3, part.length - 2);
            const key = `${TYPE_PLACEHOLDER_SKIP}${sequenceCounter}_${placeholderName}`;
            content[key] = part;
            sequenceCounter++;
            GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
            i++;
        } else if (part.startsWith('{{') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('?')) {
            const placeholderName: string = part.substring(2, part.length - 2);
            const key = `${TYPE_PLACEHOLDER}${sequenceCounter}_${placeholderName}`;
            content[key] = part;
            sequenceCounter++;
            GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
            i++;
        } else if (part.startsWith('{{#') && part.endsWith('}}')) {
            const slotNameInner: string = part.substring(3, part.length - 2);
            i++;

            const slotMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
            const { result: slotStructure, consumed: slotConsumed } = owner.parseSlotContentNew(parts, i, slotNameInner, slotMeta);
            i += slotConsumed;

            const slotWithMeta: PlainJsonObject = {};
            slotWithMeta[META_KEY] = slotMeta;
            for (const key of Object.keys(slotStructure)) {
                slotWithMeta[key] = slotStructure[key];
            }

            const key = `${TYPE_SLOTHOLDER}${sequenceCounter}_${slotNameInner}`;
            content[key] = slotWithMeta;
            sequenceCounter++;
            GenerateHtmlJson.addSlotholderToMeta(meta, slotNameInner);
        } else if (part.startsWith('{{^') && part.endsWith('}}')) {
            const negKey: string = part.substring(3, part.length - 2);
            i++;

            const negMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
            const { result: negBlockStructure, consumed: negConsumed } = owner.parseConditionalBlockContentNew(parts, i, negKey, negMeta);
            i += negConsumed;

            const key = `${TYPE_CONDITIONAL}${sequenceCounter}_${negKey}`;
            negBlockStructure['IsNegative'] = 'true';
            content[key] = negBlockStructure;
            sequenceCounter++;
        } else if (part.startsWith('{{/')) {
            break;
        } else {
            const key = `${TYPE_HTML}${sequenceCounter}`;
            content[key] = part;
            sequenceCounter++;
            i++;
        }
    }

    return { result: content, consumed: i - startIndex };
}

export const generateHtmlJsonParseMethods: GenerateHtmlJsonParseOwner = {
    convertHtmlToJson(htmlContent: string): { content: JsonObject } {
        const parts: string[] = splitByPlaceholders(htmlContent);

        const meta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
        const { result } = this.parseTemplateContentNew(parts, 0, meta);

        const finalResult: PlainJsonObject = {};
        finalResult[META_KEY] = meta;
        for (const key of Object.keys(result)) {
            finalResult[key] = result[key];
        }

        return { content: new JsonObject(finalResult) };
    },
    parseArrayBlockContentNew(parts: string[], startIndex: number, arrayKey: string, isNegative: boolean, meta: HtmlJsonMeta): ParseResult {
        const content: PlainJsonObject = {};
        let sequenceCounter = 1;
        let i = startIndex;

        while (i < parts.length) {
            const part: string = parts[i];

            if (!part) {
                i++;
                continue;
            }

            if (part.toLowerCase() === (`{{/${arrayKey}}}`).toLowerCase()) {
                i++;
                break;
            }

            if (part.startsWith('{{?') && part.endsWith('}}')) {
                const conditionalKey: string = part.substring(3, part.length - 2);
                i++;

                const condMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: conditionalBlockStructure, consumed: conditionalConsumed } = this.parseConditionalBlockContentNew(parts, i, conditionalKey, condMeta);
                i += conditionalConsumed;

                let lookAheadIdx: number = i;
                let whitespaceBetween = '';
                while (lookAheadIdx < parts.length && (!parts[lookAheadIdx] || !parts[lookAheadIdx].trim())) {
                    if (parts[lookAheadIdx]) {
                        whitespaceBetween += parts[lookAheadIdx];
                    }
                    lookAheadIdx++;
                }

                if (lookAheadIdx < parts.length && parts[lookAheadIdx] === `{{^${conditionalKey}}}`) {
                    if (whitespaceBetween) {
                        conditionalBlockStructure['WhitespaceBetween'] = whitespaceBetween;
                    }
                    i = lookAheadIdx + 1;

                    const negCondMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: negBlockStructure, consumed: negConsumed } = this.parseConditionalBlockContentNew(parts, i, conditionalKey, negCondMeta);
                    i += negConsumed;
                    conditionalBlockStructure['NegativeBlock'] = negBlockStructure;
                }

                const key = `${TYPE_CONDITIONAL}${sequenceCounter}_${conditionalKey}`;
                content[key] = conditionalBlockStructure;
                sequenceCounter++;
                continue;
            }

            if (part.startsWith('{{@') && part.endsWith('}}')) {
                const nestedArrayKey: string = part.substring(3, part.length - 2);
                if (!nestedArrayKey.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                    i++;

                    const arrayBlockMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: arrayMainBlock, consumed: arrayMainConsumed } = this.parseArrayBlockContentNew(parts, i, nestedArrayKey, false, arrayBlockMeta);
                    i += arrayMainConsumed;

                    const mainWithMeta: PlainJsonObject = {};
                    mainWithMeta[META_KEY] = arrayBlockMeta;
                    for (const key of Object.keys(arrayMainBlock)) {
                        mainWithMeta[key] = arrayMainBlock[key];
                    }

                    const arrayBlockData: PlainJsonObject = { MainBlock: mainWithMeta };

                    let lookAheadIdx: number = i;
                    let whitespaceBetween = '';
                    while (lookAheadIdx < parts.length && (!parts[lookAheadIdx] || !parts[lookAheadIdx].trim())) {
                        if (parts[lookAheadIdx]) {
                            whitespaceBetween += parts[lookAheadIdx];
                        }
                        lookAheadIdx++;
                    }

                    if (lookAheadIdx < parts.length && parts[lookAheadIdx] === `{{^${nestedArrayKey}}}`) {
                        if (whitespaceBetween) {
                            arrayBlockData['WhitespaceBetween'] = whitespaceBetween;
                        }
                        i = lookAheadIdx + 1;

                        const negArrayMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                        const { result: negArrayBlock, consumed: negArrayConsumed } = this.parseArrayBlockContentNew(parts, i, nestedArrayKey, true, negArrayMeta);
                        i += negArrayConsumed;

                        const negWithMeta: PlainJsonObject = {};
                        negWithMeta[META_KEY] = negArrayMeta;
                        for (const key of Object.keys(negArrayBlock)) {
                            negWithMeta[key] = negArrayBlock[key];
                        }
                        arrayBlockData['NegativeBlock'] = negWithMeta;
                    }

                    const key = `${TYPE_ARRAYBLOCK}${sequenceCounter}_${nestedArrayKey}`;
                    content[key] = arrayBlockData;
                    sequenceCounter++;
                    GenerateHtmlJson.addArrayBlockToMeta(meta, nestedArrayKey);
                    continue;
                }
            }

            if (part.startsWith('{{!$') && part.endsWith('}}')) {
                const jsonKeyName: string = part.substring(4, part.length - 2);
                const key = `${TYPE_JSONKEY_SKIP}${sequenceCounter}_${jsonKeyName}`;
                content[key] = part;
                sequenceCounter++;
                i++;
                continue;
            }

            if (part.startsWith('{{$') && part.endsWith('}}')) {
                const jsonKeyName: string = part.substring(3, part.length - 2);
                const key = `${TYPE_JSONKEY}${sequenceCounter}_${jsonKeyName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addJsonKeyToMeta(meta, jsonKeyName);
                i++;
                continue;
            }

            if (part.startsWith('{{!') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('$') && !part.includes('?')) {
                const placeholderName: string = part.substring(3, part.length - 2);
                const key = `${TYPE_PLACEHOLDER_SKIP}${sequenceCounter}_${placeholderName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
                i++;
                continue;
            }

            if (part.startsWith('{{') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('?')) {
                const placeholderName: string = part.substring(2, part.length - 2);
                const key = `${TYPE_PLACEHOLDER}${sequenceCounter}_${placeholderName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
                i++;
                continue;
            }

            if (part.startsWith('{{#') && part.endsWith('}}')) {
                const slotNameInner: string = part.substring(3, part.length - 2);
                i++;

                const slotMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: slotStructure, consumed: slotConsumed } = this.parseSlotContentNew(parts, i, slotNameInner, slotMeta);
                i += slotConsumed;

                const slotWithMeta: PlainJsonObject = {};
                slotWithMeta[META_KEY] = slotMeta;
                for (const key of Object.keys(slotStructure)) {
                    slotWithMeta[key] = slotStructure[key];
                }

                const key = `${TYPE_SLOTHOLDER}${sequenceCounter}_${slotNameInner}`;
                content[key] = slotWithMeta;
                sequenceCounter++;
                GenerateHtmlJson.addSlotholderToMeta(meta, slotNameInner);
                continue;
            }

            if (part.startsWith('{{^') && part.endsWith('}}')) {
                const negKey: string = part.substring(3, part.length - 2);
                i++;

                const negMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: negBlockStructure, consumed: negConsumed } = this.parseConditionalBlockContentNew(parts, i, negKey, negMeta);
                i += negConsumed;

                const key = `${TYPE_CONDITIONAL}${sequenceCounter}_${negKey}`;
                negBlockStructure['IsNegative'] = 'true';
                content[key] = negBlockStructure;
                sequenceCounter++;
                continue;
            }

            if (part.startsWith('{{/')) {
                break;
            }

            const key = `${TYPE_HTML}${sequenceCounter}`;
            content[key] = part;
            sequenceCounter++;
            i++;
        }

        const consumed = i - startIndex;
        if (isNegative) {
            return { result: content, consumed };
        }

        return { result: content, consumed };
    },
    parseArrayContentRecursiveNew(parts: string[], startIndex: number, arrayKey: string, meta: HtmlJsonMeta): ParseResult {
        const { result, consumed } = parseArrayContentRecursiveNewImpl(this, parts, startIndex, arrayKey, meta);
        return { result, consumed };
    },
    parseConditionalBlockContentNew(parts: string[], startIndex: number, conditionalKey: string, meta: HtmlJsonMeta): ParseResult {
        const blockStructure: PlainJsonObject = { ConditionalKey: conditionalKey };
        const content: PlainJsonObject = {};
        let sequenceCounter = 1;
        let i = startIndex;

        while (i < parts.length) {
            const part: string = parts[i];

            if (!part) {
                i++;
                continue;
            }

            if (part.toLowerCase() === (`{{/${conditionalKey}}}`).toLowerCase()) {
                i++;
                break;
            }

            if (part.toLowerCase() === (`{{ /${conditionalKey}}}`).toLowerCase()) {
                blockStructure['CloseTagHasSpace'] = 'true';
                i++;
                break;
            }

            if (part.startsWith('{{?') && part.endsWith('}}')) {
                const nestedConditionalKey: string = part.substring(3, part.length - 2);
                i++;

                const nestedMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: nestedConditionalBlockStructure, consumed: nestedConditionalConsumed } = this.parseConditionalBlockContentNew(parts, i, nestedConditionalKey, nestedMeta);
                i += nestedConditionalConsumed;

                const key = `${TYPE_CONDITIONAL}${sequenceCounter}_${nestedConditionalKey}`;
                content[key] = nestedConditionalBlockStructure;
                sequenceCounter++;
                continue;
            }

            if (part.startsWith('{{@') && part.endsWith('}}')) {
                const arrayKey: string = part.substring(3, part.length - 2);
                if (!arrayKey.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                    i++;

                    const arrayBlockMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: arrayMainBlock, consumed: arrayMainConsumed } = this.parseArrayBlockContentNew(parts, i, arrayKey, false, arrayBlockMeta);
                    i += arrayMainConsumed;

                    const arrayBlockData: PlainJsonObject = {};
                    const mainWithMeta: PlainJsonObject = {};
                    mainWithMeta[META_KEY] = arrayBlockMeta;
                    for (const k of Object.keys(arrayMainBlock)) {
                        mainWithMeta[k] = arrayMainBlock[k];
                    }
                    arrayBlockData['MainBlock'] = mainWithMeta;

                    let arrayLookAheadIdx: number = i;
                    let arrayWhitespaceBetween = '';
                    while (arrayLookAheadIdx < parts.length && (!parts[arrayLookAheadIdx] || !parts[arrayLookAheadIdx].trim())) {
                        if (parts[arrayLookAheadIdx]) {
                            arrayWhitespaceBetween += parts[arrayLookAheadIdx];
                        }
                        arrayLookAheadIdx++;
                    }

                    if (arrayLookAheadIdx < parts.length && parts[arrayLookAheadIdx].startsWith('{{^') && parts[arrayLookAheadIdx].endsWith('}}')) {
                        const negArrayKey: string = parts[arrayLookAheadIdx].substring(3, parts[arrayLookAheadIdx].length - 2);
                        if (negArrayKey.toLowerCase() === arrayKey.toLowerCase()) {
                            if (arrayWhitespaceBetween) {
                                arrayBlockData['WhitespaceBetween'] = arrayWhitespaceBetween;
                            }
                            i = arrayLookAheadIdx + 1;

                            const negArrayMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                            const { result: negArrayBlock, consumed: negArrayConsumed } = this.parseArrayBlockContentNew(parts, i, arrayKey, true, negArrayMeta);
                            i += negArrayConsumed;

                            const negWithMeta: PlainJsonObject = {};
                            negWithMeta[META_KEY] = negArrayMeta;
                            for (const k of Object.keys(negArrayBlock)) {
                                negWithMeta[k] = negArrayBlock[k];
                            }
                            arrayBlockData['NegativeBlock'] = negWithMeta;
                        }
                    }

                    const arrayContentKey = `${TYPE_ARRAYBLOCK}${sequenceCounter}_${arrayKey}`;
                    content[arrayContentKey] = arrayBlockData;
                    sequenceCounter++;
                    GenerateHtmlJson.addArrayBlockToMeta(meta, arrayKey);
                    continue;
                }
            }

            if (part.startsWith('{{!$') && part.endsWith('}}')) {
                const jsonKeyName: string = part.substring(4, part.length - 2);
                const key = `${TYPE_JSONKEY_SKIP}${sequenceCounter}_${jsonKeyName}`;
                content[key] = part;
                sequenceCounter++;
                i++;
                continue;
            }

            if (part.startsWith('{{$') && part.endsWith('}}')) {
                const jsonKeyName: string = part.substring(3, part.length - 2);
                const key = `${TYPE_JSONKEY}${sequenceCounter}_${jsonKeyName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addJsonKeyToMeta(meta, jsonKeyName);
                i++;
                continue;
            }

            if (part.startsWith('{{!') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('$') && !part.includes('?')) {
                const placeholderName: string = part.substring(3, part.length - 2);
                const key = `${TYPE_PLACEHOLDER_SKIP}${sequenceCounter}_${placeholderName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
                i++;
                continue;
            }

            if (part.startsWith('{{') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('?')) {
                const placeholderName: string = part.substring(2, part.length - 2);
                const key = `${TYPE_PLACEHOLDER}${sequenceCounter}_${placeholderName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
                i++;
                continue;
            }

            if (part.startsWith('{{^') && part.endsWith('}}')) {
                const negKey: string = part.substring(3, part.length - 2);
                i++;

                const negCondMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: negBlockStructure, consumed: negConsumed } = this.parseConditionalBlockContentNew(parts, i, negKey, negCondMeta);
                i += negConsumed;

                const key = `${TYPE_CONDITIONAL}${sequenceCounter}_${negKey}`;
                negBlockStructure['IsNegative'] = 'true';
                content[key] = negBlockStructure;
                sequenceCounter++;
                continue;
            }

            const key = `${TYPE_HTML}${sequenceCounter}`;
            content[key] = part;
            sequenceCounter++;
            i++;
        }

        const contentWithMeta: PlainJsonObject = {};
        contentWithMeta[META_KEY] = meta;
        for (const key of Object.keys(content)) {
            contentWithMeta[key] = content[key];
        }

        blockStructure['Content'] = contentWithMeta;
        return { result: blockStructure, consumed: i - startIndex };
    },
    parseSlotContentNew(parts: string[], startIndex: number, slotName: string, meta: HtmlJsonMeta): ParseResult {
        const slotStructure: PlainJsonObject = { SlotName: slotName };
        let i = startIndex;
        let sequenceCounter = 1;

        while (i < parts.length) {
            const part: string = parts[i];

            if (!part) {
                i++;
                continue;
            }

            if (part === `{{/${slotName}}}`) {
                i++;
                break;
            }

            if (part.startsWith('{{@') && part.endsWith('}}')) {
                const slotContentKey: string = part.substring(3, part.length - 2);
                i++;

                const contentMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: slotContent, consumed: contentConsumed } = this.parseTemplateContentNew(parts, i, contentMeta);
                i += contentConsumed;

                const contentWithMeta: PlainJsonObject = {};
                contentWithMeta[META_KEY] = contentMeta;
                for (const key of Object.keys(slotContent)) {
                    contentWithMeta[key] = slotContent[key];
                }

                slotStructure[slotContentKey] = contentWithMeta;

                if (i < parts.length && parts[i] === `{{/${slotContentKey}}}`) {
                    i++;
                }
                continue;
            }

            const key = `${TYPE_SLOTHTML}${sequenceCounter}`;
            slotStructure[key] = part;
            sequenceCounter++;
            i++;
        }

        return { result: slotStructure, consumed: i - startIndex };
    },
    parseTemplateContentNew(parts: string[], startIndex: number, meta: HtmlJsonMeta): ParseResult {
        const arrayBlocks: Record<string, PlainJsonObject> = {};
        const content: PlainJsonObject = {};
        let sequenceCounter = 1;
        let i = startIndex;

        while (i < parts.length) {
            const part: string = parts[i];

            if (!part) {
                i++;
                continue;
            }

            if ((part.startsWith('{{#') || part.startsWith('{{!#')) && part.endsWith('}}')) {
                const isSkipMarked: boolean = part.startsWith('{{!#');
                const nameStart: number = isSkipMarked ? 4 : 3;
                const slotName: string = part.substring(nameStart, part.length - 2);
                i++;

                const slotMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: slotStructure, consumed: slotConsumed } = this.parseSlotContentNew(parts, i, slotName, slotMeta);
                i += slotConsumed;

                if (isSkipMarked) {
                    slotStructure['IsSkipMarked'] = 'true';
                }

                const slotWithMeta: PlainJsonObject = {};
                slotWithMeta[META_KEY] = slotMeta;
                for (const key of Object.keys(slotStructure)) {
                    slotWithMeta[key] = slotStructure[key];
                }

                const typePrefix: string = isSkipMarked ? TYPE_SLOTHOLDER_SKIP : TYPE_SLOTHOLDER;
                const key = `${typePrefix}${sequenceCounter}_${slotName}`;
                content[key] = slotWithMeta;
                sequenceCounter++;
                GenerateHtmlJson.addSlotholderToMeta(meta, slotName);
                continue;
            }

            if (part.startsWith('{{$HTMLPLACEHOLDER') && part.endsWith('}}')) {
                const slotNameInner: string = part.substring(3, part.length - 2);
                const key = `${TYPE_SLOT}${sequenceCounter}_${slotNameInner}`;
                content[key] = part;
                sequenceCounter++;
                i++;
                continue;
            }

            if (part.startsWith('{{!$') && part.endsWith('}}')) {
                const jsonKeyName: string = part.substring(4, part.length - 2);
                const key = `${TYPE_JSONKEY_SKIP}${sequenceCounter}_${jsonKeyName}`;
                content[key] = part;
                sequenceCounter++;
                i++;
                continue;
            }

            if (part.startsWith('{{$') && part.endsWith('}}')) {
                const jsonKeyName: string = part.substring(3, part.length - 2);
                const key = `${TYPE_JSONKEY}${sequenceCounter}_${jsonKeyName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addJsonKeyToMeta(meta, jsonKeyName);
                i++;
                continue;
            }

            if (part.startsWith('{{!@') && part.endsWith('}}')) {
                const skipArrayKey: string = part.substring(4, part.length - 2);
                i++;

                const blockMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: mainBlockContent, consumed: mainConsumed } = this.parseArrayBlockContentNew(parts, i, skipArrayKey, false, blockMeta);
                i += mainConsumed;

                const arrayBlockData: PlainJsonObject = {};
                const mainWithMeta: PlainJsonObject = {};
                mainWithMeta[META_KEY] = blockMeta;
                for (const key of Object.keys(mainBlockContent)) {
                    mainWithMeta[key] = mainBlockContent[key];
                }
                arrayBlockData['MainBlock'] = mainWithMeta;
                arrayBlockData['IsSkip'] = 'true';

                let lookAheadIdx: number = i;
                let whitespaceBetween = '';
                while (lookAheadIdx < parts.length && (!parts[lookAheadIdx] || !parts[lookAheadIdx].trim())) {
                    if (parts[lookAheadIdx]) {
                        whitespaceBetween += parts[lookAheadIdx];
                    }
                    lookAheadIdx++;
                }

                if (lookAheadIdx < parts.length && parts[lookAheadIdx] === `{{^${skipArrayKey}}}`) {
                    if (whitespaceBetween) {
                        arrayBlockData['WhitespaceBetween'] = whitespaceBetween;
                    }
                    i = lookAheadIdx + 1;
                    const negMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: negBlockContent, consumed: negConsumed } = this.parseArrayBlockContentNew(parts, i, skipArrayKey, true, negMeta);
                    i += negConsumed;

                    const negWithMeta: PlainJsonObject = {};
                    negWithMeta[META_KEY] = negMeta;
                    for (const key of Object.keys(negBlockContent)) {
                        negWithMeta[key] = negBlockContent[key];
                    }
                    arrayBlockData['NegativeBlock'] = negWithMeta;
                }

                const contentKey = `${TYPE_ARRAYBLOCK_SKIP}${sequenceCounter}_${skipArrayKey}`;
                content[contentKey] = arrayBlockData;
                sequenceCounter++;
                GenerateHtmlJson.addArrayBlockToMeta(meta, skipArrayKey);
                continue;
            }

            if (part.startsWith('{{@') && part.endsWith('}}')) {
                const arrayKey: string = part.substring(3, part.length - 2);
                i++;

                const blockMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: mainBlockContent, consumed: mainConsumed } = this.parseArrayBlockContentNew(parts, i, arrayKey, false, blockMeta);
                i += mainConsumed;

                const contentKey = `${TYPE_ARRAYBLOCK}${sequenceCounter}_${arrayKey}`;
                const isFirstOccurrence: boolean = !arrayBlocks[arrayKey];

                const arrayBlockData: PlainJsonObject = {};
                arrayBlocks[arrayKey] = arrayBlockData;
                content[contentKey] = arrayBlockData;
                sequenceCounter++;
                if (isFirstOccurrence) {
                    GenerateHtmlJson.addArrayBlockToMeta(meta, arrayKey);
                }

                const mainWithMeta: PlainJsonObject = {};
                mainWithMeta[META_KEY] = blockMeta;
                for (const key of Object.keys(mainBlockContent)) {
                    mainWithMeta[key] = mainBlockContent[key];
                }
                arrayBlockData['MainBlock'] = mainWithMeta;

                let lookAheadIdx: number = i;
                let whitespaceBetween = '';
                while (lookAheadIdx < parts.length && (!parts[lookAheadIdx] || !parts[lookAheadIdx].trim())) {
                    if (parts[lookAheadIdx]) {
                        whitespaceBetween += parts[lookAheadIdx];
                    }
                    lookAheadIdx++;
                }

                if (lookAheadIdx < parts.length && parts[lookAheadIdx].startsWith('{{^') && parts[lookAheadIdx].endsWith('}}')) {
                    const nextArrayKey: string = parts[lookAheadIdx].substring(3, parts[lookAheadIdx].length - 2);
                    if (nextArrayKey.toLowerCase() === arrayKey.toLowerCase()) {
                        if (whitespaceBetween) {
                            arrayBlockData['WhitespaceBetween'] = whitespaceBetween;
                        }
                        i = lookAheadIdx;
                    }
                }
                continue;
            }

            if (part.startsWith('{{?') && part.endsWith('}}')) {
                const condKey: string = part.substring(3, part.length - 2);
                i++;

                const condMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: condBlockStructure, consumed: condConsumed } = this.parseConditionalBlockContentNew(parts, i, condKey, condMeta);
                i += condConsumed;

                let condLookAheadIdx: number = i;
                let condWhitespaceBetween = '';
                while (condLookAheadIdx < parts.length && (!parts[condLookAheadIdx] || !parts[condLookAheadIdx].trim())) {
                    if (parts[condLookAheadIdx]) {
                        condWhitespaceBetween += parts[condLookAheadIdx];
                    }
                    condLookAheadIdx++;
                }

                if (condLookAheadIdx < parts.length && parts[condLookAheadIdx] === `{{^${condKey}}}`) {
                    if (condWhitespaceBetween) {
                        condBlockStructure['WhitespaceBetween'] = condWhitespaceBetween;
                    }
                    i = condLookAheadIdx + 1;

                    const negCondMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: negCondBlockStructure, consumed: negCondConsumed } = this.parseConditionalBlockContentNew(parts, i, condKey, negCondMeta);
                    i += negCondConsumed;
                    condBlockStructure['NegativeBlock'] = negCondBlockStructure;
                }

                const condContentKey = `${TYPE_CONDITIONAL}${sequenceCounter}_${condKey}`;
                content[condContentKey] = condBlockStructure;
                sequenceCounter++;
                continue;
            }

            if (part.startsWith('{{!?') && part.endsWith('}}')) {
                const skipCondKey: string = part.substring(4, part.length - 2);
                i++;

                const skipCondMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                const { result: skipCondBlockContent, consumed: skipCondConsumed } = this.parseConditionalBlockContentNew(parts, i, skipCondKey, skipCondMeta);
                i += skipCondConsumed;
                skipCondBlockContent['IsSkip'] = 'true';

                const skipCondContentKey = `${TYPE_CONDITIONAL}${sequenceCounter}_${skipCondKey}`;
                content[skipCondContentKey] = skipCondBlockContent;
                sequenceCounter++;
                continue;
            }

            if (part.startsWith('{{^') && part.endsWith('}}')) {
                // Two distinct shapes share this marker, and they route to different processors —
                // mirroring the Normal engine, where processJsonArrayBlocks owns ONLY keys that have
                // a {{@Key}} block in the template and every other {{^Key}} falls through to
                // applyStandaloneConditionals:
                //   - PAIRED    ({{@Key}} seen earlier) -> negative ARRAY block on that array.
                //   - STANDALONE (no {{@Key}} for this key) -> negative CONDITIONAL block, the same
                //     TYPE_CONDITIONAL + IsNegative shape the nested parsers already emit.
                // Classifying a standalone block as an array block routes it to the array processor,
                // which is not a ConditionalBlock op emitter — the op then goes silently missing while
                // rendered HTML still matches.
                const negKey: string = part.substring(3, part.length - 2);
                i++;

                if (arrayBlocks[negKey]) {
                    // PAIRED: attach as the array's negative block.
                    const negMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: negativeBlockContent, consumed: negativeConsumed } = this.parseArrayBlockContentNew(parts, i, negKey, true, negMeta);
                    i += negativeConsumed;

                    const negWithMeta: PlainJsonObject = {};
                    negWithMeta[META_KEY] = negMeta;
                    for (const key of Object.keys(negativeBlockContent)) {
                        negWithMeta[key] = negativeBlockContent[key];
                    }
                    arrayBlocks[negKey]['NegativeBlock'] = negWithMeta;
                } else {
                    // STANDALONE: negative conditional block.
                    const negCondMeta: HtmlJsonMeta = GenerateHtmlJson.createMeta();
                    const { result: negBlockStructure, consumed: negCondConsumed } = this.parseConditionalBlockContentNew(parts, i, negKey, negCondMeta);
                    i += negCondConsumed;

                    const key = `${TYPE_CONDITIONAL}${sequenceCounter}_${negKey}`;
                    negBlockStructure['IsNegative'] = 'true';
                    content[key] = negBlockStructure;
                    sequenceCounter++;
                }
                continue;
            }

            if (part.startsWith('{{!') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('$') && !part.includes('?')) {
                const placeholderName: string = part.substring(3, part.length - 2);
                const key = `${TYPE_PLACEHOLDER_SKIP}${sequenceCounter}_${placeholderName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
                i++;
                continue;
            }

            if (part.startsWith('{{') && part.endsWith('}}') && !part.includes('#') && !part.includes('@') && !part.includes('/') && !part.includes('^') && !part.includes('?')) {
                const placeholderName: string = part.substring(2, part.length - 2);
                const key = `${TYPE_PLACEHOLDER}${sequenceCounter}_${placeholderName}`;
                content[key] = part;
                sequenceCounter++;
                GenerateHtmlJson.addPlaceholderToMeta(meta, placeholderName);
                i++;
                continue;
            }

            if (part.startsWith('{{/')) {
                return { result: content, consumed: i - startIndex };
            }

            const key = `${TYPE_HTML}${sequenceCounter}`;
            content[key] = part;
            sequenceCounter++;
            i++;
        }

        return { result: content, consumed: i - startIndex };
    }
};
